/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Stack } from '@fluentui/react/lib/Stack';
import { getId } from '@fluentui/react/lib/Utilities';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { EditableTitle } from 'src/core/components/EditableTitle';
import { LuisText } from 'src/core/components/LuisText';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getScopedTheme } from 'src/core/themes/luisStylist';
import { transformLabelToAnnotation } from 'src/core/utils/labelingUtils';
import { numberToPercentageString } from 'src/core/utils/number';
import { LanguageCard } from 'src/languageStudio/appShell/prebuiltDemos/components/LanguageCard';
import { commonDemosMessages } from 'src/languageStudio/appShell/prebuiltDemos/utils/commonDemoMessages';
import { resultCardStyles, resultCardTitleStyles } from 'src/languageStudio/appShell/prebuiltDemos/utils/commonStyles';
import { QuestionAnsweringResult } from 'src/languageStudio/core/services/questionsAnswering/questionsAnsweringSwagger';
import { DetectedLanguagesDocuments } from 'src/languageStudio/core/services/textAnalytics/textAnalyticsSwaggers';
import { ModelType } from 'src/luis/types/ModelType';
import { Labeler } from 'src/packages/react-text-annotator/lib/esm';

export type VisualizedQuestionAnswersResultProps = {
    document: string;
    isLabelerRTL: boolean;
    isLabelerWordTokenized: boolean;
    answers: QuestionAnsweringResult;
    detectedLanguage?: DetectedLanguagesDocuments;
};

const messages = defineMessages({
    topAnswer: {
        id: 'languageStudio.visualizedQuestionAnswersResult.topAnswer',
        defaultMessage: 'Top Answer'
    },
    longAnswerLabel: {
        id: 'languageStudio.visualizedQuestionAnswersResult.longAnswerLabel',
        defaultMessage: 'Long answer:'
    },
    shortAnswerLabel: {
        id: 'languageStudio.visualizedQuestionAnswersResult.shortAnswerLabel',
        defaultMessage: 'Short answer:'
    },
    topAnswerDescription: {
        id: 'languageStudio.visualizedQuestionAnswersResult.topAnswerDescription',
        defaultMessage: 'The JSON response will include up to three answers. The top answer has the highest confidence score.'
    }
});

const stepTheme = getScopedTheme('QuestionAnswersDemoPage');

export const VisualizedQuestionAnswersResult = (props: VisualizedQuestionAnswersResultProps) => {
    const { answers, document, isLabelerRTL, detectedLanguage, isLabelerWordTokenized } = props;

    const { localize } = useLocalization();

    const answer = answers.answers[0];

    const topAnswerCard = (
        <Stack tokens={{ childrenGap: 7 }} styles={resultCardStyles}>
            <LuisText fontWeight={500} styles={resultCardTitleStyles(stepTheme.answerLabelColor)}>
                {localize(messages.topAnswer)}
            </LuisText>
            <Stack>
                <LuisText variant="smallPlus">
                    <span style={{ fontWeight: 600 }}>{localize(messages.longAnswerLabel)}</span> {answer.answer}
                </LuisText>
                <br />
                {Boolean(answer.answerSpan) && (
                    <>
                        <LuisText variant="smallPlus">
                            <span style={{ fontWeight: 600 }}>{localize(messages.shortAnswerLabel)}</span> {answer.answerSpan.text}
                        </LuisText>
                        <br />
                    </>
                )}
                <LuisText variant="smallPlus">
                    <span style={{ fontWeight: 600 }}>{localize(commonDemosMessages.confidenceLabel)}</span>{' '}
                    {numberToPercentageString({ ratio: answer.confidenceScore })}
                </LuisText>
            </Stack>
        </Stack>
    );

    const annotations = React.useMemo(() => {
        if (!document || !answers?.answers[0]?.length) {
            return [];
        }

        const answerTokenIndices = {
            startTokenIndex: answers.answers[0].offset,
            endTokenIndex: answers.answers[0].offset + answers.answers[0].length - 1
        };

        return [
            transformLabelToAnnotation(
                {
                    depth: 1,
                    renderingLevel: 1,
                    renderedAs: 'line',
                    end: answerTokenIndices.endTokenIndex,
                    start: answerTokenIndices.startTokenIndex,
                    intrinsicEnd: answerTokenIndices.endTokenIndex,
                    intrinsicStart: answerTokenIndices.startTokenIndex,
                    entity: { id: getId(), name: localize(messages.topAnswer), roles: [], typeId: ModelType.Simple }
                },
                topAnswerCard,
                () => stepTheme.answerLabelColor
            )
        ];
    }, [answers, document]);

    return (
        <Stack tokens={{ childrenGap: 25 }} data-automation-id="questionAnswersDemoResults">
            <Stack tokens={{ childrenGap: 10 }}>
                <EditableTitle
                    descriptionAlignment="center"
                    title={localize(messages.topAnswer)}
                    description={localize(messages.topAnswerDescription)}
                    titleTextProps={{ variant: 'medium', fontWeight: 500 }}
                />
                {topAnswerCard}
            </Stack>

            <LanguageCard language={detectedLanguage?.documents[0].detectedLanguage} />

            <Stack tokens={{ childrenGap: 10 }}>
                <LuisText as="h3" fontWeight={500}>
                    {localize(commonDemosMessages.originalText)}
                </LuisText>
                <Labeler
                    text={document}
                    annotations={annotations}
                    labelerConfigs={{
                        isDisabled: true,
                        isRtl: isLabelerRTL,
                        tokenizationType: isLabelerWordTokenized ? 'word' : 'character'
                    }}
                />
            </Stack>
        </Stack>
    );
};
